import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';

import Checkbox from '../../common/Checkbox';

import { showModal } from '../../../actions/modal';

import { formatBytes } from '../../../helpers';
import EditOrLock from './common/EditOrLock';
import { fetchChangeOrderFbmDocsNecessary } from '../../../actions/supplies';
import { toast } from 'react-toastify';
import SpinnerBlock from '../../common/SpinnerBlock';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  padding-left: 0;
  padding-right: 0;
  box-shadow: none;
  border: 1px solid #ebebeb;
  > div {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 10px;
    &:last-child {
      padding-bottom: 0;
    }
  }
  .copy-tracking-number {
    position: relative;
    margin-top: -10px;
    padding-bottom: 16px !important;
    .fe-copy {
      position: absolute;
      top: 20px;
      right: 37px;
      width: 12px;
      height: 14px;
      cursor: pointer;
    }
    input {
      padding-right: 35px;
    }
  }

  .files-block {
    > div {
      display: flex;
      align-items: center;
      margin-top: 8px;
      &:first-child {
        margin-top: 0;
      }
      .file-left {
        max-width: 50%;
      }
      .file-name {
        display: block;
        color: #4fc6e1;
        font-size: 13px;
        text-decoration: underline;
        margin-right: 10px;
        text-overflow: ellipsis;
        overflow: hidden;
      }
      .dashed-line {
        flex: 1;
        border-top: 1px dashed #eceef0;
        margin-right: 10px;
      }
      .fe-printer {
        display: block;
        color: #98a6ad;
        font-size: 15px;
        margin-right: 5px;
      }
      .file-size {
        color: #bbbbbb;
        font-size: 11px;
      }
      .file-qty {
        color: #6e768e;
        font-size: 12px;
      }
    }
  }

  .card-title {
    padding-top: 3px;
    padding-bottom: 23px;
    align-items: center;
    .header-title {
      margin: 0;
      font-size: 16px;
    }
  }

  .utm-tag {
    padding-bottom: 10px;
    .utm-tag-name {
      color: #72747b;
      margin-bottom: 5px;
    }

    .utm-tag-value {
      display: flex;

      .utm-tag-string {
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0 -13px 0 0;
        transition: 0.15s;
      }

      .fe-copy {
        align-self: center;
        cursor: pointer;
        opacity: 0;
        transition: 0.15s;
      }

      &:hover {
        .utm-tag-string {
          max-width: calc(100% - 21px);
          margin: 0 8px 0 0;
        }

        .fe-copy {
          opacity: 1;
        }
      }
    }
  }

  .docs-warning {
    margin: 5px 5px 0 5px;
    padding: 10px;
  }
`;

const FBM_DOCS_UNNECESSARY_ENABLED_STATUSES = [
  'ORDER_INFO_DRAFT',
  'ORDER_INFO_NEW',
  'ORDER_INFO_NEED_CONFIRM',
  'ORDER_INFO_INSUFFICIENT_RESERVE',
  'ORDER_INFO_REQUIRE_PAYMENT',
  'ORDER_INFO_READY_TO_PACK',
  'ORDER_INFO_CONFIRM_HOLD',
  'ORDER_INFO_PACKED',
  'ORDER_INFO_PACKED_DOCUMENTS_REQUIRED',
];

const DocsBlock = (props) => {
  const {
    currentOrder,
    actualizeOrder,
    handleEditOrder,
    isNew,
    showFbmDocsUnnecessary,
    actualizeOnlyOrderByIdRequest,
  } = props;

  const dispatch = useDispatch();
  const deliveryInfo = useSelector((state) => state.orders.deliveryInfo);
  const [showDeliveryByKak2cMessage, setShowDeliveryByKak2cMessage] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const nonFFFiles = currentOrder?.files?.filter((file) => file.fileSource !== `FULFILLMENT`);
  const fulfilmentFiles = currentOrder?.files?.filter((file) => file.fileSource === `FULFILLMENT`);

  const { t } = useAutoTranslation();

  const handleOpenOrderFilesModal = () => {
    dispatch(
      showModal('OrderFiles', {
        currentOrder,
        defaultFiles: nonFFFiles,
        submitCallback: () => actualizeOrder(currentOrder.id),
      }),
    );
  };

  const handleChangePackingOptions = async (e, key) => {
    setIsFetching(true);
    const isSuccessful = await handleEditOrder({
      ...currentOrder,
      packingOptions: [
        {
          ...currentOrder.packingOptions[0],
          [key]: e.target.checked,
        },
      ],
    });

    isSuccessful
      ? toast.info("Параметр 'Упаковать документы в отдельное место' успешно изменен")
      : toast.error("Ошибка при изменении параметра 'Упаковать документы в отдельное место'");

    setIsFetching(false);
  };

  const handleChangeOrderFbmDocsNecessary = async (e) => {
    const data = {
      id: currentOrder.id,
      value: e.target.checked,
    };

    setIsFetching(true);

    await dispatch(fetchChangeOrderFbmDocsNecessary(data))
      .then(async () => {
        await actualizeOnlyOrderByIdRequest(currentOrder.id);
        toast.info("Параметр 'Документы не требуются' успешно изменен");
      })
      .catch(async (error) => {
        toast.error("Ошибка при изменении параметра 'Документы не требуются'");
      });

    setIsFetching(false);
  };

  useEffect(() => {
    if (deliveryInfo && currentOrder.delivery?.code) {
      const currentDelivery = deliveryInfo.filter(
        (item) => item.code === currentOrder.delivery.code,
      );

      if (
        currentDelivery[0].fboDeliveryByCactus &&
        currentDelivery[0].fbmType === 'ZOLOTOE_YABLOKO'
      ) {
        setShowDeliveryByKak2cMessage(true);
      }
    }
  }, [deliveryInfo, currentOrder.delivery]);

  const isFbmDocsChangeDisabled =
    FBM_DOCS_UNNECESSARY_ENABLED_STATUSES.indexOf(currentOrder?.status) === -1;

  return (
    <Wrapper className={`card-box`}>
      <div style={{ display: 'flex', position: 'relative' }} className="card-title border-bottom">
        <h4 className="header-title">{t('Документы')}</h4>
        <div style={{ position: 'absolute', height: '100px ', left: '52%' }}>
          <SpinnerBlock show={isFetching} />
        </div>
        <EditOrLock
          permissions={currentOrder.permissionList}
          permissionKey="FILES_EDITABLE"
          action={handleOpenOrderFilesModal}
        />
      </div>
      {showDeliveryByKak2cMessage ? (
        <div className="alert alert-warning point-warning docs-warning" role="alert">
          <i className="fe-alert-circle mr-2" style={{ fontSize: '20px' }} />
          <span>
            {t(
              'Для отгрузок силами Kak2C в УПД в качестве организации-грузоотправителя необходимо указывать:',
            )}
            &nbsp;
            <b>
              ООО &#171;ТЕМПОЛАЙН ЛОГИСТИКА&#187;, 105066, Город Москва, ул. Ольховская, дом 16,
              строение 5, 5А МАНСАРДА КОМНАТА А
            </b>
          </span>
        </div>
      ) : null}
      <div className="mt-3" style={{ display: 'flex', alignItems: 'center' }}>
        <div className="block-subtitle">{t('Файлы')}</div>
      </div>
      {nonFFFiles?.length ? (
        <div className="files-block">
          {nonFFFiles?.map((f) => (
            <div key={`file-${f.id}`}>
              <div className="file-left">
                <a
                  className="file-name"
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >{`${f.fileName}.${f.fileExt}`}</a>
                {f.fileSize ? <div className="file-size">{formatBytes(f.fileSize)}</div> : null}
              </div>
              <div className="dashed-line" />
              {f.printWhenPacking ? <i className="fe-printer" /> : null}
              {f.copiesQty ? <div className="file-qty"> {f.copiesQty} шт.</div> : null}
            </div>
          ))}
        </div>
      ) : (
        <div>{t('Список пуст')}</div>
      )}
      {fulfilmentFiles?.length ? (
        <>
          <div className="mt-2" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="block-subtitle">{t('Файлы с фулфилмент')}</div>
          </div>
          <div className="files-block">
            {fulfilmentFiles?.map((f) => (
              <div key={`file-${f.id}`}>
                <div className="file-left">
                  <a
                    className="file-name"
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >{`${f.fileName}.${f.fileExt}`}</a>
                  {f.fileSize ? <div className="file-size">{formatBytes(f.fileSize)}</div> : null}
                </div>
                <div className="dashed-line" />
                {f.printWhenPacking ? <i className="fe-printer" /> : null}
                {f.copiesQty ? <div className="file-qty">{f.copiesQty} шт.</div> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
      {!isNew ? (
        <div className="mt-2">
          <Checkbox
            label={t('Упаковать документы в отдельное место')}
            id={'packSpecialsToSeparatePackingPlace'}
            checked={
              currentOrder?.packingOptions?.[0]?.hasOwnProperty(
                'packSpecialsToSeparatePackingPlace',
              )
                ? currentOrder.packingOptions[0].packSpecialsToSeparatePackingPlace
                : false
            }
            onChange={(e) => handleChangePackingOptions(e, 'packSpecialsToSeparatePackingPlace')}
          />
        </div>
      ) : null}
      {showFbmDocsUnnecessary ? (
        <div className="mt-2">
          <Checkbox
            label={t('Документы не требуются')}
            id={'fbmDocsUnnecessary'}
            checked={
              currentOrder?.packingOptions?.[0]?.hasOwnProperty('fbmDocsUnnecessary')
                ? currentOrder.packingOptions[0].fbmDocsUnnecessary
                : false
            }
            onChange={(e) => handleChangeOrderFbmDocsNecessary(e)}
            isDisabled={isFbmDocsChangeDisabled}
          />
        </div>
      ) : null}
    </Wrapper>
  );
};

export default DocsBlock;
